import { useEffect, useMemo, useState } from 'react';
import { PublicUser, Team } from '../../models/types';
import { apiService } from '../../services/api.service';

// Large enough to load a whole group roster in one request.
const GROUP_MEMBER_LIMIT = 5000;

export interface RosterRow {
  userId: string;
  name: string;
  // "@username · email", shown under the name and matched by search.
  secondary: string;
  teamId: string | null;
}

export interface AssignOutcome {
  ok: number;
  failures: { userId: string; reason: string }[];
}

const displayName = (user: { username?: string; name?: string; stravaProfile?: { firstname?: string; lastname?: string } | null }, fallback: string) => {
  const strava = user.stravaProfile;
  if (strava?.firstname && strava?.lastname) return `${strava.firstname} ${strava.lastname}`;
  return user.name || user.username || fallback;
};

/** Lowercased and accent-stripped so "nguyen" matches "Nguyễn". */
export const searchKey = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

/**
 * Group members joined with their team in the contest, plus team members who have since left the group.
 * `assign` runs one request per user and keeps the change locally until fresh `teams` arrive via `onChanged`.
 */
export const useTeamRoster = (groupId: string, contestId: string, teams: Team[], onChanged: () => void) => {
  const [members, setMembers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, string | null>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await apiService.getGroupMembers(groupId, 1, GROUP_MEMBER_LIMIT);
        if (!cancelled) setMembers(result.members);
      } catch (error) {
        console.error('Error loading group members:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  // Fresh teams from the server supersede local edits.
  useEffect(() => setOverrides({}), [teams]);

  const rows = useMemo<RosterRow[]>(() => {
    const teamByUser = new Map<string, string>();
    teams.forEach(team => team.members?.forEach(member => teamByUser.set(member.userId, team._id)));

    const byUser = new Map<string, RosterRow>();
    members.forEach(user => {
      byUser.set(user._id, {
        userId: user._id,
        name: displayName(user, user._id),
        secondary: [user.username && `@${user.username}`, user.email].filter(Boolean).join(' · '),
        teamId: teamByUser.get(user._id) ?? null,
      });
    });
    teams.forEach(team =>
      team.members?.forEach(member => {
        if (!byUser.has(member.userId)) {
          byUser.set(member.userId, { userId: member.userId, name: displayName(member, member.userId), secondary: member.username ? `@${member.username}` : '', teamId: team._id });
        }
      })
    );

    return Array.from(byUser.values())
      .map(row => (row.userId in overrides ? { ...row, teamId: overrides[row.userId] } : row))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members, teams, overrides]);

  const assign = async (userIds: string[], teamId: string | null): Promise<AssignOutcome> => {
    setPending(prev => new Set(Array.from(prev).concat(userIds)));
    const outcome: AssignOutcome = { ok: 0, failures: [] };
    // Sequential: each move touches shared contest counters on the server.
    for (const userId of userIds) {
      try {
        await apiService.assignContestTeamMember(contestId, userId, teamId);
        setOverrides(prev => ({ ...prev, [userId]: teamId }));
        outcome.ok += 1;
      } catch (error) {
        outcome.failures.push({ userId, reason: error instanceof Error ? error.message : String(error) });
      }
    }
    setPending(prev => new Set(Array.from(prev).filter(id => !userIds.includes(id))));
    if (outcome.ok > 0) onChanged();
    return outcome;
  };

  return { rows, loading, loadError, pending, assign };
};
